import castArray from 'lodash/castArray'
import { GetServerSideProps } from 'next'
import React, { useContext } from 'react'

import { CMSPage } from '@Components/CMS/Page'
import { SearchAdornment } from '@Components/FormElements/Adornments/Search'
import { TextField } from '@Components/FormElements/UI/TextField'
import { ImageV2 } from '@Components/Image'
import { Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { MetadataContext } from '@Globals/Metadata'
import { PageHOC } from '@Globals/Page/Client'
import { handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { useTranslation } from '@Globals/Translations'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

interface ErrorProps extends PageProps {
  item: Page
}

type Params = {
  slug: string | string[]
}

export const getServerSideProps: GetServerSideProps<ErrorProps, Params> = async (context) => {
  const { params } = context
  const path = [
    '/errors',
    ...castArray(params?.slug || []),
  ].join('/')

  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(apolloClient, PageType.Error, path, undefined)
  const { page } = post.data

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  if (!page?.id) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...await pageProps(
        context,
        { type: 'errors', id: page.id },
        page,
        ['common', 'cms', 'footer', 'header', 'sidebar', 'validation', 'shipping', 'giftcard', 'cart', 'products'],
        false,
      ),
      item: page as Page,
    },
  }
}

const Error = PageHOC<ErrorProps>(({ item }) => {
  const { storeURL } = useContext(MetadataContext)
  const searchResultsURL = storeURL('search/results')
  const { t } = useTranslation()
  const components = useBlockComponents(item.blocks || [])

  return (
    <CMSPage>
      {components}

      {item.id === '404' && (
        <>
          <div className="form">
            <p>{t('common:404_PAGE_SHERLOCK_ON_CASE')}</p>

            <form action={searchResultsURL} method="get">
              <TextField
                adornments={{
                  end: <SearchAdornment />,
                }}
                id="th-404-search"
                label={t('common:404_PAGE_SEARCH_FIELD_TEXT')}
                name="q"
                type="text"
                withoutLabelAnimation
              />
            </form>
          </div>

          <p>{t('common:404_PAGE_DO_LIKE_MIKKEL')}</p>
          <ImageV2
            alt="404"
            height={255}
            src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt8fa38a69e3daa1fa/404-image.gif"
            width={460}
          />
        </>
      )}
    </CMSPage>
  )
})

export default Error
